import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import {
  getAllEvaluationPeriod,
  updateNoteSettings,
} from "../../api/generalParam";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

type Props = {
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

const periodSchema = z
  .object({
    id: z.string().min(1, "Periode invalide"),
    label: z.string().optional(),
    publish_note: z.boolean(),
    start_date_claim: z.string().optional(),
    end_date_claim: z.string().optional(),
    insertTeacher: z.boolean(),
    start_date_insertNote: z.string().optional(),
    end_date_insertNote: z.string().optional(),
  })
  .refine(
    (period) => {
      if (!period.start_date_claim || !period.end_date_claim) return true;
      return (
        new Date(period.start_date_claim) <= new Date(period.end_date_claim)
      );
    },
    {
      message:
        "La date debut reclamation doit etre inferieure ou egale a la date fin.",
      path: ["end_date_claim"],
    },
  )
  .refine(
    (period) => {
      if (!period.start_date_insertNote || !period.end_date_insertNote)
        return true;
      return (
        new Date(period.start_date_insertNote) <=
        new Date(period.end_date_insertNote)
      );
    },
    {
      message:
        "La date debut insertion doit etre inferieure ou egale a la date fin.",
      path: ["end_date_insertNote"],
    },
  );

const formSchema = z.object({
  periods: z.array(periodSchema),
});

type NoteSettingsFormFields = z.infer<typeof formSchema>;

const NoteSettingsModal = ({ show, onClose, onSaved }: Props) => {
  const { selectedYear } = useAcademicYear();
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<NoteSettingsFormFields>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      periods: [],
    },
  });

  const periods = watch("periods") || [];

  const normalizeDate = (dateValue: any) => {
    if (!dateValue) return "";
    return String(dateValue).slice(0, 10);
  };

  const fetchData = async () => {
    if (!selectedYear?.id) return;

    setHasLoaded(false);
    setIsLoading(true);

    try {
      const evaluationPeriods = await getAllEvaluationPeriod(selectedYear.id);
      // console.log("All Period: ", evaluationPeriods.data);
      const rows = (evaluationPeriods.data || [])
        .sort((a: any, b: any) => {
          // Tri par Level.label
          const levelCompare = a.Level.label.localeCompare(
            b.Level.label,
            "fr",
            { numeric: true },
          );

          if (levelCompare !== 0) return levelCompare;

          // Si même Level, tri par class label
          return a.label.localeCompare(b.label, "fr", { numeric: true });
        })
        .map((period: any) => ({
          id: String(period?.id || ""),
          label: String(period?.label || ""),
          publish_note: Boolean(period?.publish_note),
          start_date_claim: normalizeDate(period?.start_date_claim),
          end_date_claim: normalizeDate(period?.end_date_claim),
          insertTeacher: Boolean(period?.insertTeacher),
          start_date_insertNote: normalizeDate(period?.start_date_insertNote),
          end_date_insertNote: normalizeDate(period?.end_date_insertNote),
        }));
      reset({ periods: rows });
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    if (!show) return;
    fetchData();
  }, [selectedYear?.id, show]);

  const onSubmit = async (data: NoteSettingsFormFields) => {
    try {
      // console.log("Note settings payload:", data);
      await Promise.all(
        data.periods.map(async (period) => {
          const hasPublicationDates =
            period.start_date_claim && period.end_date_claim;

          const hasInsertionDates =
            period.start_date_insertNote && period.end_date_insertNote;

          if (!hasPublicationDates && !hasInsertionDates) return;
          await updateNoteSettings(period.id, {
            publish_note: period.publish_note,
            start_date_claim: period.start_date_claim,
            end_date_claim: period.end_date_claim,
            insertTeacher: period.insertTeacher,
            start_date_insertNote: period.start_date_insertNote,
            end_date_insertNote: period.end_date_insertNote,
          });
        }),
      );
      onSaved?.();
      onClose();
      toast.success("Paramètres enregitré avec succès !");
    } catch (error: any) {
      // console.log("Erreur lors de l'enregistrement des paramètres: ", error);
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Parametres des Notes</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <ul className="nav nav-tabs nav-tabs-bottom mb-3">
            <li className="nav-item">
              <a
                className="nav-link active"
                href="#publication-notes"
                data-bs-toggle="tab"
              >
                Publication des notes
              </a>
            </li>
            <li className="nav-item">
              <a
                className="nav-link"
                href="#insertion-notes"
                data-bs-toggle="tab"
              >
                Insertion par les profs
              </a>
            </li>
          </ul>

          <div className="tab-content">
            <div className="tab-pane show active" id="publication-notes">
              <table className="table table-striped table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Semestre</th>
                    <th scope="col">Date debut reclamation</th>
                    <th scope="col">Date fin reclamation</th>
                    <th scope="col">Publie</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && !hasLoaded && (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        Chargement des periodes...
                      </td>
                    </tr>
                  )}

                  {!isLoading && periods.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        Aucune donnee
                      </td>
                    </tr>
                  )}

                  {!isLoading &&
                    periods.map((period, idx) => (
                      <tr key={period.id || idx}>
                        <td>
                          {period.label || "_"}
                          <input
                            type="hidden"
                            {...register(`periods.${idx}.id`)}
                          />
                          <input
                            type="hidden"
                            {...register(`periods.${idx}.label`)}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            {...register(`periods.${idx}.start_date_claim`)}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            {...register(`periods.${idx}.end_date_claim`)}
                          />
                          {errors?.periods?.[idx]?.end_date_claim?.message && (
                            <small className="text-danger">
                              {String(
                                errors.periods[idx]?.end_date_claim?.message,
                              )}
                            </small>
                          )}
                        </td>
                        <td className="text-center">
                          <div className="status-toggle modal-status">
                            <input
                              type="checkbox"
                              id={`toggle-${period.id || idx}`}
                              disabled={
                                !watch(`periods.${idx}.start_date_claim`) ||
                                !watch(`periods.${idx}.end_date_claim`)
                              }
                              className="check"
                              {...register(`periods.${idx}.publish_note`)}
                            />
                            <label
                              htmlFor={`toggle-${period.id || idx}`}
                              className="checktoggle"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="tab-pane" id="insertion-notes">
              <table className="table table-striped table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Semestre</th>
                    <th scope="col">Date debut insertion</th>
                    <th scope="col">Date fin insertion</th>
                    <th scope="col">Autoriser</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && !hasLoaded && (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        Chargement des periodes...
                      </td>
                    </tr>
                  )}

                  {!isLoading && periods.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        Aucune donnee
                      </td>
                    </tr>
                  )}

                  {!isLoading &&
                    periods.map((period, idx) => (
                      <tr key={period.id || idx}>
                        <td>
                          {period.label || "_"}
                          <input
                            type="hidden"
                            {...register(`periods.${idx}.id`)}
                          />
                          <input
                            type="hidden"
                            {...register(`periods.${idx}.label`)}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            {...register(
                              `periods.${idx}.start_date_insertNote`,
                            )}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            {...register(`periods.${idx}.end_date_insertNote`)}
                          />
                          {errors?.periods?.[idx]?.end_date_insertNote
                            ?.message && (
                            <small className="text-danger">
                              {String(
                                errors.periods[idx]?.end_date_insertNote
                                  ?.message,
                              )}
                            </small>
                          )}
                        </td>
                        <td className="text-center">
                          <div className="status-toggle modal-status">
                            <input
                              type="checkbox"
                              id={`insert-toggle-${period.id || idx}`}
                              disabled={
                                !watch(
                                  `periods.${idx}.start_date_insertNote`,
                                ) ||
                                !watch(`periods.${idx}.end_date_insertNote`)
                              }
                              className="check"
                              {...register(`periods.${idx}.insertTeacher`)}
                            />
                            <label
                              htmlFor={`insert-toggle-${period.id || idx}`}
                              className="checktoggle"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="rounded mb-3"></div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="submit"
            className="btn btn-info me-3"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? "Enregistrement..." : "Enregistrer"}
          </button>
          <button
            type="button"
            className="btn btn-danger me-3"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Fermer
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default withPermission(
  NoteSettingsModal,
  [buildPermission(Resource.LEVEL, Action.CREATE)],
  "masquer",
);
